
import { useEffect } from 'react';
import { isTauri } from '@/lib/utils/tauri';

/**
 * Prevents the backspace key from navigating back in history when not in an input field.
 * This is necessary for Tauri desktop apps since the webview inherits browser-like
 * navigation behavior that's not appropriate for desktop applications.
 *
 * The hook only activates when running in Tauri (desktop mode).
 */
export function usePreventBackspaceNavigation(): void {
  useEffect(() => {
    // Only prevent backspace navigation in Tauri desktop mode
    if (!isTauri()) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle backspace key
      if (event.key !== 'Backspace') return;

      const target = event.target as HTMLElement;

      // Allow backspace in editable elements
      const isEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]') !== null;

      // If not in an editable element, prevent the default navigation behavior
      if (!isEditable) {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
}
